import { useEffect, useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { requestService, RequestFilters } from '../services/requestService'
import api from '../services/api'
import {
  LicenseRequest,
  REQUEST_TYPE_LABELS,
  REQUEST_STATUS_LABELS,
  WORK_CENTER_LABELS,
  SHIFT_LABELS,
} from '../types'
import { formatDate } from '../utils/dateUtils'
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  FileSpreadsheet,
  FileText,
  LayoutGrid,
  List,
  Calendar,
  User,
  Briefcase,
  Eye,
  Trash2,
  Ban,
  RefreshCcw,
  AlertTriangle,
  Info,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'

// Hook para detectar si es móvil
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return isMobile
}

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'APPROVED':
      return <CheckCircle className="h-4 w-4" />
    case 'DENIED':
      return <XCircle className="h-4 w-4" />
    case 'CANCELLED':
      return <Ban className="h-4 w-4" />
    default:
      return <Clock className="h-4 w-4" />
  }
}

const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'APPROVED':
      return 'badge-success'
    case 'DENIED':
      return 'badge-error'
    case 'CANCELLED':
      return 'badge-gray'
    default:
      return 'badge-warning'
  }
}

// Componente Card para vista móvil
interface RequestCardProps {
  request: LicenseRequest
  isAdmin: boolean
  onView: (request: LicenseRequest) => void
  onDelete: (request: LicenseRequest) => void
  onCancel: (request: LicenseRequest) => void
}

function RequestCard({ request, isAdmin, onView, onDelete, onCancel }: RequestCardProps) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-3 sm:p-4 shadow-sm">
      {/* Header con empleado y estado */}
      <div className="flex items-start justify-between gap-2 sm:gap-3 mb-2 sm:mb-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-gradient-to-br from-secondary-400 to-secondary-600 flex items-center justify-center text-white font-semibold text-xs sm:text-sm flex-shrink-0">
            {request.employeeName.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white truncate">
              {request.employeeId} - {request.employeeName}
            </p>
            <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
              {request.employeePosition} • {SHIFT_LABELS[request.employeeShift] || request.employeeShift}
            </p>
          </div>
        </div>
        <span className={`badge ${getStatusBadgeClass(request.status)} flex-shrink-0 text-[10px] sm:text-xs px-1.5 sm:px-2.5 py-0.5 sm:py-1`}>
          <StatusIcon status={request.status} />
          <span className="ml-0.5 sm:ml-1">{request.statusName}</span>
        </span>
      </div>

      {/* Tipo de solicitud */}
      <div className="mb-2 sm:mb-3">
        <div className="flex items-center gap-1.5 sm:gap-2 text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">
          <Briefcase className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          <span className="text-[10px] sm:text-xs font-medium uppercase">Tipo</span>
        </div>
        <p className="text-xs sm:text-sm text-gray-900 dark:text-white pl-5 sm:pl-6 line-clamp-2">
          {request.requestTypeName}
        </p>
      </div>

      {/* Fechas y días */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-2 sm:mb-3">
        <div>
          <div className="flex items-center gap-1.5 sm:gap-2 text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">
            <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span className="text-[10px] sm:text-xs font-medium uppercase">Fechas</span>
          </div>
          <p className="text-xs sm:text-sm text-gray-900 dark:text-white pl-5 sm:pl-6">
            <span className="block sm:inline">{formatDate(request.startDate)}</span>
            {request.secondDate ? (
              <>
                <span className="hidden sm:inline"> y </span>
                <span className="block sm:inline">{formatDate(request.secondDate)}</span>
              </>
            ) : request.startDate !== request.endDate ? (
              <>
                <span className="hidden sm:inline"> - </span>
                <span className="block sm:inline">{formatDate(request.endDate)}</span>
              </>
            ) : null}
          </p>
        </div>
        <div>
          <div className="flex items-center gap-1.5 sm:gap-2 text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">
            <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span className="text-[10px] sm:text-xs font-medium uppercase">Días</span>
          </div>
          <p className="text-base sm:text-lg font-bold text-gray-900 dark:text-white pl-5 sm:pl-6">
            {request.daysRequested}
          </p>
        </div>
      </div>

      {/* Centro y turno */}
      <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-2 sm:mb-3">
        <User className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
        <span className="truncate">{WORK_CENTER_LABELS[request.employeeWorkCenter]}</span>
        <span>•</span>
        <span>{formatDate(request.requestedAt)}</span>
      </div>

      {/* Motivo de denegación si existe */}
      {request.status === 'DENIED' && request.denialReason && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-3">
          <p className="text-xs text-red-600 dark:text-red-400">
            <span className="font-medium">Motivo:</span> {request.denialReason}
          </p>
        </div>
      )}

      {/* Motivo de cancelación si existe */}
      {request.status === 'CANCELLED' && request.cancellationReason && (
        <div className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-3">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            <span className="font-medium">Motivo de cancelación:</span> {request.cancellationReason}
          </p>
        </div>
      )}

      {/* Procesado/Cancelado por */}
      {request.status !== 'PENDING' && (
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          {request.status === 'CANCELLED' ? (
            <>
              <span className="font-medium">Cancelado por:</span>{' '}
              {request.cancelledByName || 'Sistema'}
              {request.cancelledAt && (
                <span className="ml-1">
                  ({formatDate(request.cancelledAt)})
                </span>
              )}
            </>
          ) : (() => {
            const isAutomatic = !request.processedByName || request.processedByName === 'Sistema (automático)'
            return isAutomatic ? (
              <>
                <span className="font-medium">{request.status === 'APPROVED' ? 'Aprobado' : 'Denegado'} automáticamente</span>
                {request.processedAt && (
                  <span className="ml-1">
                    ({formatDate(request.processedAt)})
                  </span>
                )}
              </>
            ) : (
              <>
                <span className="font-medium">{request.status === 'APPROVED' ? 'Aprobado' : 'Denegado'} por:</span>{' '}
                {request.processedByName}
                {request.processedAt && (
                  <span className="ml-1">
                    ({formatDate(request.processedAt)})
                  </span>
                )}
              </>
            )
          })()}
        </div>
      )}

      {/* Botones de acción */}
      <div className="flex gap-2 pt-3 border-t border-gray-100 dark:border-slate-700">
        <button
          onClick={() => onView(request)}
          className="flex-1 btn btn-secondary py-2 text-sm flex items-center justify-center gap-2"
        >
          <Eye className="h-4 w-4" />
          Ver detalle
        </button>
        {/* Cancelar solicitudes aprobadas */}
        {isAdmin && request.status === 'APPROVED' && (
          <button
            onClick={() => onCancel(request)}
            className="p-2 hover:bg-warning-50 dark:hover:bg-warning-900/30 rounded-lg text-warning-600 dark:text-warning-400 transition-colors"
            title="Cancelar solicitud"
          >
            <Ban className="h-4 w-4" />
          </button>
        )}
        {/* Eliminar solicitudes pendientes o denegadas */}
        {isAdmin && (request.status === 'PENDING' || request.status === 'DENIED') && (
          <button
            onClick={() => onDelete(request)}
            className="p-2 hover:bg-error-50 dark:hover:bg-error-900/30 rounded-lg text-error-600 dark:text-error-400 transition-colors"
            title="Eliminar"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}

export function RequestsPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isMobile = useIsMobile()
  const [requests, setRequests] = useState<LicenseRequest[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards') // Por defecto cards
  const [filters, setFilters] = useState<RequestFilters>({
    page: 1,
    limit: 10,
    status: searchParams.get('status') as any || undefined,
  })
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  })
  const [isExporting, setIsExporting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [resetConfirmText, setResetConfirmText] = useState('')
  const [isResetting, setIsResetting] = useState(false)
  const [cancellationReason, setCancellationReason] = useState('')
  const [selectedRequest, setSelectedRequest] = useState<LicenseRequest | null>(null)
  const [requestsEnabled, setRequestsEnabled] = useState<boolean>(true)

  // Verificar si las solicitudes están habilitadas
  useEffect(() => {
    const checkStatus = async () => {
      // Si es admin, siempre puede crear
      if (user?.role === 'ADMIN') {
        setRequestsEnabled(true)
        return
      }
      try {
        const status = await requestService.getRequestsStatus()
        setRequestsEnabled(status.requestsEnabled)
      } catch {
        setRequestsEnabled(true) // En caso de error, asumimos habilitado
      }
    }
    checkStatus()
  }, [user?.role])

  // En desktop, por defecto tabla; en móvil, por defecto cards
  useEffect(() => {
    setViewMode(isMobile ? 'cards' : 'table')
  }, [])

  const fetchRequests = async () => {
    setIsLoading(true)
    try {
      const response = await requestService.getAll(filters)
      setRequests(response.data)
      setPagination(response.pagination)
    } catch (error) {
      console.error('Error fetching requests:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchRequests()
  }, [filters])

  const handleSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search, page: 1 }))
  }

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  const isAdmin = user?.role === 'ADMIN'
  const canExport = user?.role === 'ADMIN'

  const handleView = (request: LicenseRequest) => {
    navigate(`/requests/${request.id}`)
  }

  const handleDelete = async () => {
    if (!selectedRequest) return
    try {
      await requestService.delete(selectedRequest.id)
      toast.success('Solicitud eliminada', {
        description: 'La solicitud ha sido eliminada correctamente.',
      })
      setShowDeleteConfirm(false)
      setSelectedRequest(null)
      fetchRequests()
    } catch (error: any) {
      toast.error('Error al eliminar', {
        description: error.response?.data?.message || 'No se pudo eliminar la solicitud.',
      })
    }
  }

  const handleCancel = async () => {
    if (!selectedRequest) return
    try {
      await requestService.cancel(selectedRequest.id, cancellationReason || undefined)
      toast.success('Solicitud cancelada', {
        description: isAdmin
          ? 'La solicitud ha sido cancelada y el empleado ha sido notificado.'
          : 'Se ha notificado al responsable de RRHH.',
      })
      setShowCancelConfirm(false)
      setSelectedRequest(null)
      setCancellationReason('')
      fetchRequests()
    } catch (error: any) {
      toast.error('Error al cancelar', {
        description: error.response?.data?.message || 'No se pudo cancelar la solicitud.',
      })
    }
  }

  const handleExportExcel = async () => {
    setIsExporting(true)
    try {
      await requestService.exportExcel(filters)
      toast.success('Excel descargado', {
        description: 'El archivo se ha descargado correctamente.',
      })
    } catch (error) {
      console.error('Error exporting:', error)
      toast.error('Error al exportar', {
        description: 'No se pudo generar el archivo Excel.',
      })
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportPDF = async () => {
    setIsExporting(true)
    try {
      await requestService.exportPDF(filters)
      toast.success('PDF descargado', {
        description: 'El archivo se ha descargado correctamente.',
      })
    } catch (error) {
      console.error('Error exporting:', error)
      toast.error('Error al exportar', {
        description: 'No se pudo generar el archivo PDF.',
      })
    } finally {
      setIsExporting(false)
    }
  }

  const handleReset = async () => {
    if (resetConfirmText !== 'CONFIRMAR_RESET') {
      toast.error('Código incorrecto', {
        description: 'Escribe CONFIRMAR_RESET para continuar.',
      })
      return
    }

    setIsResetting(true)
    try {
      const response = await api.post('/configuration/reset', { confirmCode: 'CONFIRMAR_RESET' })
      const data = response.data
      toast.success('Reset completado', {
        description: `Se eliminaron ${data.deleted.requests} solicitudes, ${data.deleted.requestHistory} historiales y ${data.deleted.notifications} notificaciones.`,
      })
      setShowResetConfirm(false)
      setResetConfirmText('')
      fetchRequests()
    } catch (error: any) {
      console.error('Error resetting:', error)
      toast.error('Error al resetear', {
        description: error.response?.data?.error || 'No se pudo completar el reset.',
      })
    } finally {
      setIsResetting(false)
    }
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-slide-in">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title flex items-center gap-2 sm:gap-3 text-xl sm:text-2xl lg:text-3xl">
            <div className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-gradient-to-br from-secondary-500 to-secondary-700 shadow-sm">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            Solicitudes
          </h1>
          <p className="page-subtitle text-sm sm:text-base">
            {pagination.total} solicitudes en total
            <span className="hidden md:inline text-gray-400 dark:text-gray-500"> • Aprobación/denegación automática según reglas del convenio</span>
          </p>
        </div>

        {/* Actions - responsive grid */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Toggle vista cards/tabla - oculto en móvil pequeño */}
          <div className="hidden sm:flex rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
            <button
              onClick={() => setViewMode('cards')}
              className={`p-2 transition-colors ${
                viewMode === 'cards'
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                  : 'bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
              title="Vista de tarjetas"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 transition-colors border-l border-gray-200 dark:border-slate-700 ${
                viewMode === 'table'
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                  : 'bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
              title="Vista de tabla"
            >
              <List className="h-4 w-4" />
            </button>
          </div>

          {canExport && (
            <>
              <button
                onClick={handleExportExcel}
                disabled={isExporting}
                className="btn btn-secondary flex items-center gap-1.5 sm:gap-2 text-sm px-2.5 sm:px-4 py-2"
                title="Exportar a Excel"
              >
                <FileSpreadsheet className="h-4 w-4" />
                <span className="hidden sm:inline">Excel</span>
              </button>
              <button
                onClick={handleExportPDF}
                disabled={isExporting}
                className="btn btn-secondary flex items-center gap-1.5 sm:gap-2 text-sm px-2.5 sm:px-4 py-2"
                title="Exportar a PDF"
              >
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">PDF</span>
              </button>
            </>
          )}

          {requestsEnabled ? (
            <Link to="/requests/new" className="btn btn-primary flex items-center gap-1.5 sm:gap-2 text-sm px-3 sm:px-4 py-2 ml-auto sm:ml-0">
              <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden xs:inline">Nueva</span>
              <span className="hidden sm:inline">Solicitud</span>
            </Link>
          ) : (
            <button
              disabled
              className="btn btn-primary flex items-center gap-1.5 sm:gap-2 text-sm px-3 sm:px-4 py-2 opacity-50 cursor-not-allowed ml-auto sm:ml-0"
              title="Las solicitudes están temporalmente deshabilitadas"
            >
              <Ban className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">Deshabilitado</span>
            </button>
          )}

          {/* Reset button - Solo para testing */}
          {isAdmin && (
            <button
              onClick={() => setShowResetConfirm(true)}
              className="btn bg-red-600 hover:bg-red-700 text-white flex items-center gap-1.5 sm:gap-2 text-sm px-2.5 sm:px-4 py-2"
              title="Resetear solicitudes (testing)"
            >
              <RefreshCcw className="h-4 w-4" />
              <span className="hidden sm:inline">Reset</span>
            </button>
          )}
        </div>
      </div>

      {/* Banner de solicitudes deshabilitadas (solo para empleados) */}
      {!isAdmin && !requestsEnabled && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-start gap-3 animate-slide-in">
          <Info className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              Creación de solicitudes deshabilitada
            </p>
            <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
              La creación de nuevas solicitudes está temporalmente deshabilitada. Si necesitas realizar una solicitud urgente, contacta con el departamento de RRHH.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-3 sm:p-4 lg:p-6">
        <div className="flex flex-col gap-3 sm:gap-4">
          {/* Búsqueda por empleado - Solo para admin */}
          {isAdmin && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por empleado..."
                className="input pl-9 sm:pl-10 w-full text-sm sm:text-base py-2 sm:py-2.5"
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          )}
          <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 sm:gap-3">
            <select
              className="input text-sm py-2 sm:py-2.5"
              value={filters.status || ''}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  status: e.target.value as any || undefined,
                  page: 1,
                }))
              }
              title="Las solicitudes válidas se aprueban automáticamente, las inválidas se deniegan automáticamente."
            >
              <option value="">Todos los estados</option>
              {Object.entries(REQUEST_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className="input text-sm py-2 sm:py-2.5"
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  requestType: e.target.value as any || undefined,
                  page: 1,
                }))
              }
            >
              <option value="">Todos los tipos</option>
              {Object.entries(REQUEST_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Content - Cards or Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
        </div>
      ) : viewMode === 'cards' ? (
        /* Vista de Cards */
        <div className="space-y-4">
          {requests.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
              <FileText className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-gray-500 dark:text-gray-400">No hay solicitudes</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {requests.map((request) => (
                <RequestCard
                  key={request.id}
                  request={request}
                  isAdmin={isAdmin}
                  onView={handleView}
                  onDelete={(req) => {
                    setSelectedRequest(req)
                    setShowDeleteConfirm(true)
                  }}
                  onCancel={(req) => {
                    setSelectedRequest(req)
                    setShowCancelConfirm(true)
                  }}
                />
              ))}
            </div>
          )}

          {/* Pagination para Cards */}
          {requests.length > 0 && (
            <div className="flex flex-col xs:flex-row items-center justify-between gap-3 p-3 sm:p-4 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 text-center xs:text-left">
                <span className="hidden xs:inline">Mostrando </span>
                {(pagination.page - 1) * pagination.limit + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)}
                <span className="hidden xs:inline"> de</span><span className="xs:hidden">/</span> {pagination.total}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed p-2 sm:px-3 sm:py-2"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-500 dark:text-gray-400 min-w-[60px] text-center">
                  {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed p-2 sm:px-3 sm:py-2"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Vista de Tabla */
        <div className="table-container">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="table-header">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                    Empleado
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                    Fechas
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                    Días
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                    Solicitado
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.id} className="table-row">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-secondary-400 to-secondary-600 flex items-center justify-center text-white font-semibold text-sm">
                          {request.employeeName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {request.employeeId} - {request.employeeName}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {request.employeePosition} • {SHIFT_LABELS[request.employeeShift] || request.employeeShift} • {WORK_CENTER_LABELS[request.employeeWorkCenter]}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white max-w-xs">
                        {request.requestTypeName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {formatDate(request.startDate)}
                      </div>
                      {request.secondDate ? (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          y {formatDate(request.secondDate)}
                        </div>
                      ) : request.startDate !== request.endDate ? (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          hasta {formatDate(request.endDate)}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-lg font-semibold text-gray-900 dark:text-white">
                        {request.daysRequested}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`badge ${getStatusBadgeClass(request.status)}`}>
                        <StatusIcon status={request.status} />
                        <span className="ml-1">{request.statusName}</span>
                      </span>
                      {request.status === 'CANCELLED' ? (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          por {request.cancelledByName || 'Sistema'}
                        </div>
                      ) : request.status !== 'PENDING' && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {(!request.processedByName || request.processedByName === 'Sistema (automático)') ? (
                            <>automático</>
                          ) : (
                            <>por {request.processedByName}</>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {formatDate(request.requestedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-1">
                        {/* Ver detalle */}
                        <button
                          onClick={() => handleView(request)}
                          className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-600 dark:text-gray-400 transition-colors"
                          title="Ver detalle"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {/* Cancelar - solo admin y solicitudes aprobadas */}
                        {isAdmin && request.status === 'APPROVED' && (
                          <button
                            onClick={() => {
                              setSelectedRequest(request)
                              setShowCancelConfirm(true)
                            }}
                            className="p-2 hover:bg-warning-50 dark:hover:bg-warning-900/30 rounded-lg text-warning-600 dark:text-warning-400 transition-colors"
                            title="Cancelar solicitud"
                          >
                            <Ban className="h-4 w-4" />
                          </button>
                        )}
                        {/* Eliminar - solo admin y solicitudes pendientes/denegadas */}
                        {isAdmin && (request.status === 'PENDING' || request.status === 'DENIED') && (
                          <button
                            onClick={() => {
                              setSelectedRequest(request)
                              setShowDeleteConfirm(true)
                            }}
                            className="p-2 hover:bg-error-50 dark:hover:bg-error-900/30 rounded-lg text-error-600 dark:text-error-400 transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination para Tabla */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-slate-800/50">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Mostrando {(pagination.page - 1) * pagination.limit + 1} a{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)}{' '}
              de {pagination.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false)
          setSelectedRequest(null)
        }}
        onConfirm={handleDelete}
        title="Eliminar solicitud"
        message={`¿Estás seguro de que deseas eliminar esta solicitud de ${selectedRequest?.employeeName}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
      />

      {/* Reset Confirmation Dialog */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => {
              setShowResetConfirm(false)
              setResetConfirmText('')
            }} />
            <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6 z-50">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Resetear datos de testing
              </h3>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-2">
                  Esta acción eliminará:
                </p>
                <ul className="text-sm text-red-600 dark:text-red-400 list-disc ml-4 space-y-1">
                  <li>Todas las solicitudes</li>
                  <li>Todo el historial de solicitudes</li>
                  <li>Todas las notificaciones</li>
                </ul>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
                Escribe <strong className="text-red-600">CONFIRMAR_RESET</strong> para continuar:
              </p>
              <input
                type="text"
                className="input w-full mb-4 font-mono"
                placeholder="CONFIRMAR_RESET"
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowResetConfirm(false)
                    setResetConfirmText('')
                  }}
                  className="btn btn-secondary"
                  disabled={isResetting}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleReset}
                  disabled={isResetting || resetConfirmText !== 'CONFIRMAR_RESET'}
                  className="btn bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                >
                  {isResetting ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Reseteando...
                    </span>
                  ) : (
                    'Resetear todo'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Dialog */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => {
              setShowCancelConfirm(false)
              setSelectedRequest(null)
              setCancellationReason('')
            }} />
            <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6 z-50">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <Ban className="h-5 w-5 text-warning-600" />
                Cancelar solicitud
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                ¿Estás seguro de que deseas cancelar la solicitud aprobada de <strong>{selectedRequest?.employeeName}</strong>?
                <br />
                <span className="text-sm">El empleado será notificado por email y notificación en la aplicación.</span>
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Motivo de cancelación (opcional)
                </label>
                <textarea
                  className="input w-full min-h-[80px]"
                  placeholder="Ej: El empleado ha solicitado anular la licencia..."
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowCancelConfirm(false)
                    setSelectedRequest(null)
                    setCancellationReason('')
                  }}
                  className="btn btn-secondary"
                >
                  Volver
                </button>
                <button
                  onClick={handleCancel}
                  className="btn bg-warning-600 hover:bg-warning-700 text-white"
                >
                  Cancelar solicitud
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
